import {
  Box,
  ButtonBase,
  Container,
  Grid,
  Rating,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import React, { useState } from "react";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowBack from "@mui/icons-material/ArrowBack";
import AppReviewDisplayCard, { IAppReview } from "./AppReviewDisplayCard";
import { AppStoreRating, GooglePlayRating } from "../../../constants/text";
import StarOutlineFilled from "../../icons/StarOutlineFilled";
import StarOutlineEmpty from "../../icons/StarOutlineEmpty";
import Display from "../../layout/Display";
import COLORS from "../../../constants/colors";
import Split from "../../../utils/arrays";

interface Props {
  bgcolor?: string;
  reviews: IAppReview[];
}

const StaticFourPointFiveStar = () => (
  <Rating
    precision={0.5}
    readOnly
    value={4.5}
    size="medium"
    icon={
      <Box pr="4px">
        <StarOutlineFilled fontSize="inherit" />
      </Box>
    }
    emptyIcon={
      <Box pr="4px">
        <StarOutlineEmpty fontSize="inherit" />
      </Box>
    }
  />
);

const ReviewShowCase: React.FC<Props> = ({ bgcolor, reviews }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % reviews.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + reviews.length) % reviews.length
    );
  };
  return (
    <Box pb={{ xs: "48px", md: "110px" }} pt="48px">
      <Container
        maxWidth="lg"
        component="section"
        sx={{
          bgcolor: { xs: bgcolor, md: COLORS.TRANSPARENT[100] },
        }}
      >
        <Box
          bgcolor={bgcolor}
          borderRadius={{ md: "20px", sx: "0px" }}
          px={{ sx: "0px", md: "55px", lg: "110px" }}
          py={{ sx: "24px", md: "170px", lg: "160px" }}
        >
          <Stack flexDirection="row">
            <Stack pt={{ xs: "20px" }} spacing="25px">
              <Typography
                variant="h2"
                fontFamily="Erlik"
                color={COLORS.BLUE[100]}
              >
                FORNØYDE BRUKERE
              </Typography>
              <Stack
                direction={{ md: "row", xs: "column" }}
                spacing={{ md: "34px", xs: "20px" }}
                paddingRight={{ md: "0px", xs: "0px" }}
              >
                <ButtonBase
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    borderRadius: "20px",
                    p: "12px",
                  }}
                  target="_blank"
                  href="https://apps.apple.com/no/app/horde-sjef-i-egen-%C3%B8konomi/id1471960734?platform=iphone"
                >
                  <Typography component="u" color="primary">
                    App Store
                  </Typography>
                  <Typography component="u" color="primary">
                    {AppStoreRating}
                  </Typography>
                  <StaticFourPointFiveStar />
                </ButtonBase>
                <ButtonBase
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    borderRadius: "20px",
                    p: "12px",
                  }}
                  target="_blank"
                  href="https://play.google.com/store/apps/details?id=com.horde&hl=no"
                >
                  <Typography component="u" color="primary">
                    Google Play
                  </Typography>
                  <Typography component="u" color="primary">
                    {GooglePlayRating}
                  </Typography>
                  <StaticFourPointFiveStar />
                </ButtonBase>
              </Stack>
            </Stack>
            <Display desktop>
              <Stack
                bgcolor={bgcolor}
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  position: "relative",
                }}
              >
                {reviews.map((review, index) => (
                  <Stack
                    key={index}
                    sx={{
                      position: "absolute",
                      left: { md: "26px", lg: "90px" },
                      width: { md: "430px", lg: "430px" },
                      height: "300px",
                      display: "block",
                      transition: "opacity 0.4s ease-in-out",
                      opacity: index === currentIndex ? 1 : 0,
                      "@media (max-width: 995px)": {
                        width: "345px", // Endre bredden til 300px når skjermbredden er mindre enn 995px
                      },
                    }}
                  >
                    <AppReviewDisplayCard
                      title={review.title}
                      content={review.content}
                      reviewerName={review.reviewerName}
                      dateOfReview={review.dateOfReview}
                      starsNr={review.starsNr}
                    />
                  </Stack>
                ))}

                <Button
                  onClick={handlePrev}
                  sx={{
                    position: "absolute",
                    bottom: { md: "-120px", lg: "-110px" },
                    left: { md: "390px", lg: "460px" },
                    maxWidth: "40px",
                    backgroundColor: COLORS.BLACK[100],
                    color: COLORS.WHITE[100],
                    "@media (max-width: 995px)": {
                      left: "310px",
                      bottom: "-160px",
                    },
                  }}
                >
                  <ArrowForward />
                </Button>
                <Button
                  onClick={handleNext}
                  sx={{
                    position: "absolute",
                    bottom: { md: "-120px", lg: "-110px" },
                    right: { md: "-360px", lg: "-430px" },
                    maxWidth: "40px",
                    backgroundColor: COLORS.BLACK[100],
                    color: COLORS.WHITE[100],
                    "@media (max-width: 995px)": {
                      left: "220px",
                      bottom: "-160px",
                    },
                  }}
                >
                  <ArrowBack />
                </Button>
              </Stack>
            </Display>
          </Stack>
        </Box>
      </Container>
      <Display mobile>
        <Box
          bgcolor={bgcolor}
          display="flex"
          sx={{
            overflowX: "scroll",
            scrollSnapType: "x mandatory",
            "& > *": {
              scrollSnapAlign: "center",
            },
            "::-webkit-scrollbar": { display: "none" },
            gap: "40px",
            pt: "32px",
            pb: "20px",
            px: "15px",
          }}
        >
          {reviews.map((review, index) => {
            return (
              <Grid item display="flex" minWidth="350px" key={index}>
                <AppReviewDisplayCard
                  title={review.title}
                  content={review.content}
                  reviewerName={review.reviewerName}
                  dateOfReview={review.dateOfReview}
                  starsNr={review.starsNr}
                />
              </Grid>
            );
          })}
        </Box>
      </Display>
    </Box>
  );
};
export default ReviewShowCase;
